// api client
import { client } from '../api/client'

// types
import {
  Message,
  EmployeeMessage,
  EmployeeMessageResponse,
  CreateEmployeeMessagePayload,
  PatchEmployeeMessagePayload
} from '../types/communicateTypes'

/**
 * Fetch all messages
 *
 * @param id
 * @returns Promise<Message[]>
 */
const fetchAllMessages = (id: number): Promise<Message[]> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.get(`/OnBoarding/${id}/Messages`)

      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not fetch data' })
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Reply to message
 *
 * @param id
 * @param messageKey
 * @param data
 * @returns
 */
const replyToMessage = (
  id: number,
  messageKey: number,
  data: { message: string }
): Promise<Message> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.patch(
        `/OnBoarding/${id}/Message/${messageKey}`,
        data
      )

      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not reply to this message' })
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Create new message
 *
 * @param id
 * @param data
 * @returns
 */
const createNewMessage = (
  id: number,
  data: { message: string; subject: string }
): Promise<number> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.post(`/OnBoarding/${id}/Message`, data)

      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not reply to this message' })
      }
    } catch (error) {
      reject(error)
    }
  })
}
const messagesCount = (id: number): Promise<Message[]> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.get(
        `/OnBoarding/GetUnreadMessageCount?Id=${id}`
      )

      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not fetch data' })
      }
    } catch (error) {
      reject(error)
    }
  })
}

const getEmployeesByDept = (
  entityId: number,
  storeId: number,
  deptId: number
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.get(
        `/OnBoarding/GetEmployeeListByDept?EntityID=${entityId}&StoreId=${storeId}&DeptId=${deptId}`
      )
      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'could not fetch data' })
      }
    } catch (err) {
      reject(err)
    }
  })
}

const fetchEmployeeMessages = (
  onboardingKey: number
): Promise<EmployeeMessageResponse> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.get(
        `/OnBoarding/${onboardingKey}/EmployeeMessages`
      )

      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not fetch employee messages' })
      }
    } catch (error) {
      reject(error)
    }
  })
}

const createEmployeeMessage = (
  onboardingKey: number,
  data: CreateEmployeeMessagePayload
): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.post(
        `/OnBoarding/${onboardingKey}/EmployeeMessage`,
        data
      )

      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not send employee message' })
      }
    } catch (error) {
      reject(error)
    }
  })
}

const patchEmployeeMessage = (
  onboardingKey: number,
  essEmployeeMessageKey: number,
  data: PatchEmployeeMessagePayload
): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.patch(
        `/OnBoarding/${onboardingKey}/EmployeeMessage/${essEmployeeMessageKey}`,
        data
      )

      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not update employee message' })
      }
    } catch (error) {
      reject(error)
    }
  })
}

const getChattedEmployees = (obKey: number) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.get(`/OnBoarding/${obKey}/ChattedEmployees`)
      if (response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'Could not get employee message' })
      }
    } catch (err) {
      reject(err)
    }
  })
}

const getConversation = (obkey: number, pUserKey?: number, pObKey?: number) => {
  return new Promise(async (resolve, reject) => {
    try {
      let url = `/OnBoarding/${obkey}/EmployeeMessages`
      if (pUserKey !== undefined && pObKey !== undefined) {
        url += `?partnerUserKey=${pUserKey}&partnerOnboardingKey=${pObKey}`
      }
      const response = await client.get(url)
      if (response && response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'could not get data' })
      }
    } catch (err) {
      reject(err)
    }
  })
}

const updateIsEmployeeMessagingEnabled = (
  id: number,
  isEnabled: boolean
): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await client.patch(
        `/OnBoarding/UpdateIsEmployeeMessagingEnabled?Id=${id}&IsEmployeeMessagingEnabled=${isEnabled}`
      )
      if (response && response.status === 200) {
        resolve(response.data)
      } else {
        reject({ message: 'could not update messaging permission' })
      }
    } catch (err) {
      reject(err)
    }
  })
}

export const communicateService = {
  fetchAllMessages,
  replyToMessage,
  createNewMessage,
  messagesCount,
  getEmployeesByDept,
  fetchEmployeeMessages,
  createEmployeeMessage,
  patchEmployeeMessage,
  getChattedEmployees,
  getConversation,
  updateIsEmployeeMessagingEnabled
}
